import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";

const AuthContext = createContext();

export const useAuth = () => {
  console.log(API_BASE_URL);
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      checkAuthStatus();
    } else {
      setLoading(false);
    }
  }, [token]);

  const checkAuthStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/accounts/me/`);
      setUser(response.data);
    } catch (error) {
      console.error("Auth check failed:", error);
      localStorage.removeItem("token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  // Google OAuth login
  const loginWithGoogle = async (idToken) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/google/callback/`,
        {
          id_token: idToken,
        }
      );

      const { access, user: userData } = response.data;
      localStorage.setItem("token", access);
      setToken(access);
      setUser(userData);

      // Track successful login
      if (typeof gtag !== "undefined") {
        gtag("event", "login", {
          method: "google",
          event_category: "authentication",
          event_label: "success",
        });
      }

      return true;
    } catch (error) {
      console.error("Login error:", error);

      // Track failed login
      if (typeof gtag !== "undefined") {
        gtag("event", "login", {
          method: "google",
          event_category: "authentication",
          event_label: "failed",
        });
      }

      return false;
    }
  };

  // Email/Password login
  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login/`, {
        email,
        password,
      });

      const { access, user: userData } = response.data;
      localStorage.setItem("token", access);
      setToken(access);
      setUser(userData);

      // Track successful login
      if (typeof gtag !== "undefined") {
        gtag("event", "login", {
          method: "email",
          event_category: "authentication",
          event_label: "success",
        });
      }

      return true;
    } catch (error) {
      console.error("Login error:", error);

      // Track failed login
      if (typeof gtag !== "undefined") {
        gtag("event", "login", {
          method: "email",
          event_category: "authentication",
          event_label: "failed",
        });
      }

      return false;
    }
  };

  // Email/Password signup
  const signup = async (userData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/register/`, {
        email: userData.email,
        password: userData.password,
        first_name: userData.first_name,
        last_name: userData.last_name,
      });

      const { access, user: newUser } = response.data;
      localStorage.setItem("token", access);
      setToken(access);
      setUser(newUser);

      // Track successful signup
      if (typeof gtag !== "undefined") {
        gtag("event", "sign_up", {
          method: "email",
          event_category: "authentication",
          event_label: "success",
        });
      }

      return true;
    } catch (error) {
      console.error("Signup error:", error);

      // Track failed signup
      if (typeof gtag !== "undefined") {
        gtag("event", "sign_up", {
          method: "email",
          event_category: "authentication",
          event_label: "failed",
        });
      }

      return false;
    }
  };

  const logout = () => {
    // Track logout
    if (typeof gtag !== "undefined") {
      gtag("event", "logout", {
        event_category: "authentication",
        event_label: "user_initiated",
      });
    }

    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    loginWithGoogle,
    signup,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
